import { useState } from "react";
import { Copy, Mail, RefreshCw, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { BottomSheet, ChalkActionButton, SectionTitle } from "../chalk/primitives";
import { copyToClipboard } from "../../lib/copyToClipboard";
import { tournaments } from "../../lib/tournamentApi";
import { inputStyle } from "./shared";

export default function TeamSheet({ data, team, isDirector, locked, onClose, onChanged }) {
  const t = data.tournament;
  const hub = t.tier === "hub";
  const [captainName, setCaptainName] = useState(team.captain_name || "");
  const [captainEmail, setCaptainEmail] = useState(team.captain_email || "");
  const [moveTo, setMoveTo] = useState(team.division_id);

  async function run(fn, ok) {
    try { await fn(); if (ok) toast.success(ok); await onChanged(); }
    catch (e) { toast.error(e.message || "Something went wrong."); }
  }
  const saveCaptain = () => run(() => tournaments.patchTeam(t.id, team.id,
    { captain_name: captainName, captain_email: captainEmail }), "Captain saved.");

  return (
    <BottomSheet isOpen title={team.name} onDismiss={onClose}>
      <div data-testid="team-sheet" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {hub && (
          <>
            <SectionTitle label="Captain" />
            <input value={captainName} onChange={(e) => setCaptainName(e.target.value)} placeholder="Captain name"
                   data-testid="captain-name" style={inputStyle} />
            <input value={captainEmail} onChange={(e) => setCaptainEmail(e.target.value)} placeholder="captain@email.com"
                   type="email" data-testid="captain-email" style={inputStyle} />
            <ChalkActionButton label="Save captain" variant="ghost" onClick={saveCaptain} testId="captain-save" />
            {isDirector && <CheckinLinkBlock t={t} team={team} run={run} />}
            <Roster t={t} team={team} players={(data.players || []).filter((p) => p.team_id === team.id)} run={run} />
          </>
        )}
        {hub && isDirector && !locked && data.divisions.length > 1 && (
          <div style={{ display: "flex", gap: 6 }}>
            <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)} data-testid="team-move-division"
                    style={{ ...inputStyle, flex: 1 }}>
              {data.divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <ChalkActionButton label="Move" fullWidth={false} testId="team-move-submit"
                               onClick={() => run(() => tournaments.patchTeam(t.id, team.id, { division_id: moveTo }), "Moved.").then(onClose)} />
          </div>
        )}
        {isDirector && !locked && (
          <button data-testid="team-delete" onClick={() => window.confirm(`Remove ${team.name}?`)
                    && run(() => tournaments.deleteTeam(t.id, team.id), "Team removed.").then(onClose)}
                  style={{ background: "transparent", border: "0.5px solid var(--chalk-red)", color: "var(--chalk-red)",
                           borderRadius: 10, padding: "8px 12px", cursor: "pointer", fontSize: 13 }}>
            Remove team
          </button>
        )}
      </div>
    </BottomSheet>
  );
}

function CheckinLinkBlock({ t, team, run }) {
  const url = team.checkin_path ? `${window.location.origin}/t/${t.slug}/${team.checkin_path}` : null;
  const iconBtn = { background: "transparent", border: "0.5px solid var(--chalk-border)", borderRadius: 8,
                    padding: "6px 8px", cursor: "pointer", color: "var(--green-mid)" };
  return (
    <div data-testid="checkin-link-block" style={{ background: "#FBF8F2", borderRadius: 10, padding: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700 }}>Captain check-in link</div>
      {url ? (
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
          <code data-testid="checkin-link-url" style={{ flex: 1, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{url}</code>
          <button aria-label="Copy link" data-testid="checkin-link-copy" style={iconBtn}
                  onClick={async () => toast[(await copyToClipboard(url)) ? "success" : "error"]("Link copied.")}>
            <Copy size={14} aria-hidden="true" />
          </button>
          <button aria-label="Email captain" data-testid="checkin-link-email" style={iconBtn}
                  onClick={() => run(() => tournaments.checkinLink(t.id, team.id, { send_email: true }), "Emailed to captain.")}>
            <Mail size={14} aria-hidden="true" />
          </button>
          <button aria-label="Regenerate link" data-testid="checkin-link-regenerate" style={iconBtn}
                  onClick={() => window.confirm("Make a new link? The old one stops working.")
                    && run(() => tournaments.checkinLink(t.id, team.id, { regenerate: true }), "New link created.")}>
            <RefreshCw size={14} aria-hidden="true" />
          </button>
          <button aria-label="Revoke link" data-testid="checkin-link-revoke" style={{ ...iconBtn, color: "var(--chalk-red)" }}
                  onClick={() => run(() => tournaments.revokeCheckinLink(t.id, team.id), "Link revoked.")}>
            <X size={14} aria-hidden="true" />
          </button>
        </div>
      ) : (
        <ChalkActionButton label="Create link" variant="ghost" testId="checkin-link-create"
                           onClick={() => run(() => tournaments.checkinLink(t.id, team.id, {}), "Link created.")} />
      )}
      <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 6 }}>
        Unique to this team. Expires when the tournament ends.
      </div>
    </div>
  );
}

function Roster({ t, team, players, run }) {
  const [first, setFirst] = useState("");
  const [initial, setInitial] = useState("");
  const [year, setYear] = useState("");
  const [jersey, setJersey] = useState("");
  async function add() {
    if (!first.trim() || !initial.trim()) return toast.error("First name and last initial are required.");
    await run(() => tournaments.addPlayer(t.id, team.id, {
      first_name: first, last_initial: initial, birth_year: year ? Number(year) : null, jersey: jersey || null,
    }));
    setFirst(""); setInitial(""); setYear(""); setJersey("");
  }
  return (
    <div data-testid="roster-block">
      <SectionTitle label={`Roster (${players.length})`} />
      <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginBottom: 6 }}>
        {t.team_type === "youth" ? "Youth rosters are never shown publicly." : "Public page shows first name, last initial and jersey only."}
        {" "}Birth year is for eligibility only and is never public.
      </div>
      {players.map((p) => (
        <div key={p.id} data-testid={`player-row-${p.id}`} style={{ display: "flex", alignItems: "center", gap: 8,
                                                                    padding: "6px 0", borderBottom: "0.5px solid var(--chalk-border)", fontSize: 13 }}>
          <span style={{ width: 28, color: "var(--chalk-muted)" }}>{p.jersey ? `#${p.jersey}` : ""}</span>
          <span style={{ flex: 1 }}>{p.first_name} {p.last_initial}.</span>
          <span style={{ color: "var(--chalk-muted)", fontSize: 11 }}>{p.birth_year || ""}</span>
          <button aria-label="Remove player" data-testid={`player-remove-${p.id}`}
                  onClick={() => run(() => tournaments.removePlayer(t.id, team.id, p.id))}
                  style={{ background: "transparent", border: "none", color: "var(--chalk-muted)", cursor: "pointer" }}>
            <Trash2 size={14} aria-hidden="true" />
          </button>
        </div>
      ))}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 0.7fr 1fr 0.7fr", gap: 6, marginTop: 8 }}>
        <input value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First" data-testid="player-first" style={inputStyle} />
        <input value={initial} maxLength={1} onChange={(e) => setInitial(e.target.value)} placeholder="L" data-testid="player-initial" style={inputStyle} />
        <input value={year} inputMode="numeric" maxLength={4} onChange={(e) => setYear(e.target.value.replace(/\D/g, ""))} placeholder="Birth yr" data-testid="player-year" style={inputStyle} />
        <input value={jersey} maxLength={3} onChange={(e) => setJersey(e.target.value)} placeholder="#" data-testid="player-jersey" style={inputStyle} />
      </div>
      <div style={{ marginTop: 6 }}>
        <ChalkActionButton label="Add player" variant="ghost" onClick={add} testId="player-add" />
      </div>
    </div>
  );
}
